import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as pub from "@/api/public";
import * as users from "@/api/users";

// ═══════════════════════════════════════════════════════════════════
// Two surfaces that are easy to confuse, side by side on purpose.
//
// LEFT — the PUBLIC surface: everything a stranger can read with no account.
// It is the landing page's data supply and the only part of WeDeshi somebody
// sees before deciding whether to join. Note what it does NOT return: the
// community endpoints give COUNTS and sampled content, never people. The whole
// argument for a public preview collapses if it doubles as a scrape target.
//
// RIGHT — the MEMBER's own view: completeness, the two people-discovery lists,
// and the three "mine" lists. These are NOT the Identity Board. The board
// matches on who somebody is, scored across dozens of dimensions; `nearby` and
// `from-my-state` answer the two plainer questions people ask first, and
// neither needs a completed board.
//
// "Near me" is a METRO, not a radius — a measured choice. When the obvious
// distance rule was tested it failed: San Francisco is 42.0 miles from San Jose
// and inside the hour, Berkeley is 42.5 and outside it, because the bay is in
// the way. No radius separates them; drive time is the real rule.
// ═══════════════════════════════════════════════════════════════════

export default function PublicSurfaceLab() {
  const [stateCode, setStateCode] = useState("GJ");
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const say = (m: string) => setLog((l) => [m, ...l].slice(0, 10));
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  const map = useQuery({ queryKey: ["pub-map"], queryFn: pub.getStatesMap });
  const regions = useQuery({ queryKey: ["pub-india-regions"], queryFn: pub.getIndiaRegions });
  const completeness = useQuery({
    queryKey: ["me-completeness"],
    queryFn: users.getMyCompleteness,
  });
  // The "Your journey here" sheet's help, chosen on the server from my answers.
  const journeyHelp = useQuery({
    queryKey: ["me-journey-help"],
    queryFn: users.getJourneyHelp,
  });
  // The "What you do" sheet's count: members near me in each field of work.
  const nearWork = useQuery({
    queryKey: ["me-near-counts", "work"],
    queryFn: () => users.getNearCounts("work"),
  });
  // The "Where your family is from" sheet's count: members near me with roots
  // in each state. A member who keeps their roots to themselves is not in it.
  const nearRoots = useQuery({
    queryKey: ["me-near-counts", "roots"],
    queryFn: () => users.getNearCounts("roots"),
  });

  const states = map.data?.data ?? [];
  const list = regions.data?.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Public surface & my lists"
        subtitle="What a stranger can read without an account, and what only you can."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Public" }]}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ── Public ─────────────────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <div>
            <h2 className="text-sm font-medium text-gray-900">No account needed</h2>
            <p className="text-xs text-gray-500">
              Counts and sampled content only — never people.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <input
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 3))}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-20"
            />
            <span className="text-xs text-gray-500">state code</span>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getStateExperience(stateCode);
                  return `experience ${stateCode}: ${r.data.greeting ?? "no greeting"} · ${r.data.patterns.length} patterns`;
                })
              }
            >
              Experience
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getStateAssets(stateCode);
                  return `assets: map ${r.data.mapSvgUrl ? "✔" : "—"} · ${r.data.patternUrls.length} patterns · audio ${r.data.ambientAudioUrl ? "✔" : "—"}`;
                })
              }
            >
              Assets
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getCommunityStats(stateCode);
                  return `stats: ${r.data.memberCount} members across ${r.data.cityCounts.length} cities`;
                })
              }
            >
              Community stats
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getCommunityPreview(stateCode);
                  return `preview keys: ${Object.keys(r.data).join(", ") || "empty"}`;
                })
              }
            >
              Community preview
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getPublicDefaults();
                  return `defaults: ${Object.keys(r.data).length} keys`;
                })
              }
            >
              Defaults
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getPublicEventsPreview();
                  return `events preview: ${Object.keys(r.data).join(", ") || "empty"}`;
                })
              }
            >
              Events preview
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await pub.getPublicNostalgiaPreview();
                  return `nostalgia preview: ${Object.keys(r.data).join(", ") || "empty"}`;
                })
              }
            >
              Nostalgia preview
            </Button>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-900 mb-1">
              States map {map.isLoading ? "…" : `(${states.length})`}
            </p>
            <div className="flex flex-wrap gap-1">
              {states.slice(0, 40).map((s) => (
                <Badge key={s.code} variant={s.primaryColor ? "green" : "gray"}>
                  {s.code}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Green = the state has its own theme.
            </p>
          </div>
        </section>

        {/* ── Mine ───────────────────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <div>
            <h2 className="text-sm font-medium text-gray-900">Only you</h2>
            <p className="text-xs text-gray-500">
              Discovery lists and the three "mine" lists.
            </p>
          </div>

          {completeness.data && (
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">
                {completeness.data.data.percentage}%
              </div>
              <div className="text-xs text-gray-500">profile completeness</div>
              <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                {completeness.data.data.suggestions.slice(0, 3).map((s) => (
                  <li key={s}>· {s}</li>
                ))}
              </ul>
            </div>
          )}

          {journeyHelp.data && (
            <div>
              <div className="text-xs font-medium text-gray-700">Help near you, from your journey answers</div>
              {!journeyHelp.data.data.answered ? (
                <p className="text-xs text-gray-500">Answer "Your journey here" to see visa, tax and legal help.</p>
              ) : journeyHelp.data.data.sections.length === 0 ? (
                <p className="text-xs text-gray-500">Your answers ask for no help.</p>
              ) : (
                <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                  {journeyHelp.data.data.sections.map((section) => (
                    <li key={section.key}>
                      · {section.title}:{" "}
                      {section.providers.length > 0
                        ? section.providers.map((p) => p.name).join(", ")
                        : "nobody listed near you yet"}
                    </li>
                  ))}
                  {journeyHelp.data.data.events.map((e) => (
                    <li key={e.id}>· Clinic: {e.title} ({e.eventDate.slice(0, 10)})</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {nearWork.data && (
            <div>
              <div className="text-xs font-medium text-gray-700">
                Near {nearWork.data.data.place ?? "you"}, by field of work
              </div>
              {nearWork.data.data.counts.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No field has {nearWork.data.data.floor} or more members near you yet.
                </p>
              ) : (
                <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                  {nearWork.data.data.counts.map((c) => (
                    <li key={c.value}>
                      · {c.sentence}
                      {c.value === nearWork.data.data.mine && <span className="text-gray-400"> (yours)</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {nearRoots.data && (
            <div>
              <div className="text-xs font-medium text-gray-700">
                Near {nearRoots.data.data.place ?? "you"}, by the family's state
              </div>
              {nearRoots.data.data.counts.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No state has {nearRoots.data.data.floor} or more members near you yet.
                </p>
              ) : (
                <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                  {nearRoots.data.data.counts.map((c) => (
                    <li key={c.value}>
                      · {c.sentence}
                      {c.value === nearRoots.data.data.mine && <span className="text-gray-400"> (yours)</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await users.getNearbyUsers({ limit: 5 });
                  return `nearby (metro, not radius): ${r.data.users.map((u) => u.name).join(", ") || "nobody"}`;
                })
              }
            >
              Near me
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await users.getUsersFromMyState({ limit: 5 });
                  return `same roots: ${r.data.users.map((u) => u.name).join(", ") || "nobody"}`;
                })
              }
            >
              From my state
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await users.getMyFollowedOrganizers();
                  return `following ${r.data.organizers.length} organisers`;
                })
              }
            >
              Followed organisers
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await users.getMyWatchlist();
                  return `watchlist: ${r.data.watchlist.length} events saved without committing`;
                })
              }
            >
              Watchlist
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  const r = await users.getMyOrders();
                  return `orders: ${r.data.orders.length}`;
                })
              }
            >
              My orders
            </Button>
          </div>
        </section>
      </div>

      {/* ── The official list ───────────────────────────────── */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-sm font-medium text-gray-900">
            India&apos;s states and union territories {list ? `(${list.regions.length})` : "…"}
          </h2>
          {list && <Badge variant="blue">version {list.version}</Badge>}
          {list && <span className="text-xs text-gray-500">owner: {list.owner}</span>}
          <Button
            size="sm"
            variant="secondary"
            isLoading={busy}
            disabled={!list}
            onClick={() =>
              run(async () => {
                const status = await pub.checkIndiaRegionsEtag(`"${list!.version}"`);
                return `GET /public/reference/india-regions with If-None-Match → ${status}${status === 304 ? " (your copy is current)" : ""}`;
              })
            }
          >
            Ask again with the ETag
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          One sourced list: the First Schedule names each place, the Local Government Directory gives its code, and
          ISO 3166-2:IN its letters. A former code (TG, DN…) is accepted forever and maps to today&apos;s.
        </p>
        {list && (
          <>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {list.sources.map((source) => (
                <li key={source.field}>
                  <span className="font-medium">{source.field}</span>: {source.source} (checked {source.checkedOn})
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1 pr-3">Code</th>
                    <th className="py-1 pr-3">LGD</th>
                    <th className="py-1 pr-3">Name</th>
                    <th className="py-1 pr-3">Kind</th>
                    <th className="py-1 pr-3">Own script</th>
                    <th className="py-1 pr-3">Former codes</th>
                  </tr>
                </thead>
                <tbody>
                  {list.regions.map((r) => (
                    <tr key={r.code} className="border-t border-gray-100">
                      <td className="py-1 pr-3 font-mono">{r.code}</td>
                      <td className="py-1 pr-3">{r.lgdCode}</td>
                      <td className="py-1 pr-3">{r.name}</td>
                      <td className="py-1 pr-3">{r.kind === "union_territory" ? "union territory" : r.kind}</td>
                      <td className="py-1 pr-3">
                        {r.nameNative ? `${r.nameNative} (${r.nativeLanguage})` : "—"}
                        {r.nameNative && !r.nameNativeConfirmed && <span className="text-gray-400"> · unconfirmed</span>}
                      </td>
                      <td className="py-1 pr-3 font-mono">{r.aliases.join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">
              Retired:{" "}
              {list.retired.map((r) => `${r.code} ${r.name} → ${r.successor} (since ${r.since})`).join(" · ")}
            </p>
          </>
        )}
      </section>

      {log.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-gray-900 mb-2">What came back</h2>
          <ul className="text-xs text-gray-600 space-y-1 font-mono">
            {log.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
